import { ArrowRight, Check, Clock } from "lucide-react";
import { blueprintPaper, primaryButton, secondaryButton, textButton } from "./ui";

const STEPS = ["Tell us the problem", "Tell us how you work", "Get your personal queue"];
const PREVIEW = [
  { label: "The problem", value: "Starts here", active: true },
  { label: "How you work", value: "Fills in as you answer" },
  { label: "Your everyday tools", value: "Fills in as you answer" },
];

/** The catalog's front door: what happens, how long it takes, and a preview of the blueprint. */
export default function Welcome({ resume, onStart, onBrowse, onResume, onStartOver }: {
  resume: string | null;
  onStart: () => void;
  onBrowse: () => void;
  onResume: () => void;
  onStartOver: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-12 pb-6 pt-2 sm:pt-6">
      <div className="catalog-stagger flex min-w-0 flex-[999_1_480px] flex-col gap-6">
        
        <div className="flex flex-col gap-4">
          <h1 className="mb-0 font-display text-4xl font-bold leading-tight text-text-primary sm:text-5xl">
            Find the automations made for your business
          </h1>
          <p className="max-w-xl text-lg leading-relaxed text-text-secondary sm:text-xl">
            Answer a few questions and get a short list of ready-made applications picked for how your team works.
          </p>
        </div>
        <ul className="flex flex-wrap gap-x-6 gap-y-2 font-semibold text-text-secondary">
          <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" aria-hidden="true" />About 3 minutes</li>
          <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" aria-hidden="true" />No sign-up</li>
          <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" aria-hidden="true" />No technical knowledge needed</li>
        </ul>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <button type="button" onClick={onStart} className={`${primaryButton} group px-8 py-4 text-lg`}>
            Start my assessment
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </button>
          <button type="button" onClick={onBrowse} className={textButton}>Or browse everything</button>
        </div>
        <ol className="catalog-stagger flex flex-wrap gap-3">
          {STEPS.map((step, i) => (
            <li key={step} className="flex flex-[1_1_160px] items-center gap-2.5 rounded-xl border border-borderSubtle bg-white/80 px-4 py-3">
              <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary/10 text-sm font-extrabold text-primary">{i + 1}</span>
              <span className="text-[15px] font-semibold text-text-primary">{step}</span>
            </li>
          ))}
        </ol>
        {resume && (
          <div className="flex flex-col gap-3 rounded-2xl border-2 border-primary/30 bg-surface p-5 shadow-lg shadow-primary/10">
            <p className="font-display text-lg font-bold text-text-primary">Welcome back. Pick up where you left off.</p>
            <p className="text-text-secondary">{resume}</p>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={onResume} className={primaryButton}>Pick up where I left off</button>
              <button type="button" onClick={onStartOver} className={secondaryButton}>Start over</button>
            </div>
          </div>
        )}
      </div>
      <div className="catalog-pop min-w-0 flex-[1_1_340px] [animation-delay:250ms]" aria-hidden="true">
        <div style={blueprintPaper} className="flex flex-col gap-3 rounded-2xl border border-primary/20 p-6 shadow-xl">
          <div className="flex items-baseline justify-between">
            <span className="font-display text-lg font-bold text-text-primary">Your blueprint</span>
            <span className="text-sm font-bold text-text-muted">0 of 7 pieces</span>
          </div>
          {PREVIEW.map((p) => (
            <div key={p.label} className={`flex flex-col rounded-xl border-2 border-dashed px-4 py-3 ${p.active ? "border-primary bg-white" : "border-slate-300 bg-white/60"}`}>
              <span className={`text-xs font-bold ${p.active ? "text-primary" : "text-text-muted"}`}>{p.label}</span>
              <span className={`text-[15px] ${p.active ? "font-semibold text-primary" : "text-text-muted"}`}>{p.value}</span>
            </div>
          ))}
          <div className="flex flex-col gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-white/60 px-4 py-3">
            <span className="text-xs font-bold text-text-muted">Your applications</span>
            <span className="flex gap-2"><span className="h-2.5 flex-[3] rounded-full bg-slate-200" /><span className="h-2.5 flex-[2] rounded-full bg-slate-200" /><span className="h-2.5 flex-[2] rounded-full bg-slate-200" /></span>
          </div>
          <p className="text-sm text-text-muted">Every answer adds a piece. When it is complete, your queue is ready.</p>
        </div>
      </div>
    </div>
  );
}

export function BuildingQueue({ step, rows }: { step: number; rows: { label: string; value: string }[] }) {
  return (
    <div role="region" aria-labelledby="building-title" aria-live="polite"
             className="mx-auto flex w-full max-w-3xl flex-col gap-5 rounded-2xl border border-borderSubtle bg-surface p-6 shadow-xl sm:p-9">
      <h2 id="building-title" className="font-display text-2xl font-bold text-text-primary sm:text-3xl">Building your queue</h2>
      <p className="text-lg text-text-secondary">Fitting your answers together, piece by piece.</p>
      <div aria-hidden="true" className="h-2.5 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-500" style={{ width: `${Math.min(100, step * 25)}%` }} />
      </div>
      <ol className="flex flex-col gap-2.5">
        {rows.map((row, i) => {
          const done = step > i;
          return (
            <li key={row.label}
                className={`flex items-center gap-3.5 rounded-xl border px-4 py-3.5 motion-safe:transition-all motion-safe:duration-500 ${
                  done ? "border-primary/30 bg-primary/5" : "border-borderSubtle bg-white"} ${step >= i ? "opacity-100" : "opacity-30"}`}>
              <span aria-hidden="true" className={`flex h-7 w-7 flex-none items-center justify-center rounded-full text-white ${done ? "bg-primary" : "bg-slate-200"}`}>
                {done && <Check className="h-4 w-4" />}
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="text-xs font-bold text-text-muted">{row.label}</span>
                <span className="font-bold text-text-primary">{row.value}</span>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
